<template>
  <div class="dt-wrap">

    <!-- Controls -->
    <div class="dt-controls">
      <div class="dt-left">
        <select v-model="perPageLocal" class="dt-perpage">
          <option v-for="n in [20, 50, 100, 500]" :key="n" :value="n">{{ n }} por página</option>
        </select>
        <span class="dt-info">
          {{ from + 1 }}–{{ to }} de {{ total }}
          <span v-if="totalSelected > 0" class="dt-info-sel"> · {{ totalSelected }} selecionados</span>
        </span>
      </div>
      <div class="dt-right">
        <div class="dt-pag">
          <button class="dt-pag-btn" :disabled="page <= 1" @click="$emit('page-changed', page - 1)">‹</button>
          <button
            v-for="p in pageNums" :key="p"
            class="dt-pag-btn" :class="{ cur: p === page }"
            @click="$emit('page-changed', p)"
          >{{ p }}</button>
          <button class="dt-pag-btn" :disabled="page >= totalPages" @click="$emit('page-changed', page + 1)">›</button>
        </div>
      </div>
    </div>

    <!-- Dica de uso (só na primeira vez que não há seleções) -->
    <div v-if="rows.length > 0 && totalSelected === 0" class="dt-hint">
      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="12" r="10"/><path d="M12 16v-4M12 8h.01"/></svg>
      Clique num valor de desconto para editá-lo · Selecione clientes para atualizar em lote
    </div>

    <!-- Tabela vazia -->
    <div v-if="rows.length === 0" class="dt-empty">
      <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.2"><circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/></svg>
      <p>Nenhum cliente encontrado</p>
      <button v-if="hasFilters" class="dt-empty-btn" @click="$emit('clear-filters')">Limpar filtros</button>
    </div>

    <!-- Tabela -->
    <div v-else class="dt-scroll">
      <table class="dt-table">
        <thead>
          <tr>
            <th class="dt-th dt-th-check">
              <span class="dt-cb" :class="{ on: allPageSelected, partial: somePageSelected }" @click="$emit('toggle-page-select')">
                <svg v-if="allPageSelected" width="8" height="8" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="4"><path d="M20 6 9 17l-5-5"/></svg>
                <span v-else-if="somePageSelected" class="dt-cb-dash"></span>
              </span>
            </th>
            <th class="dt-th dt-th-name">Cliente</th>
            <th
              v-for="r in restaurantes" :key="r.id"
              class="dt-th dt-th-disc"
              :class="{ sorted: sortRestId === r.id }"
              @click="onSort(r.id)"
            >
              <span class="dt-th-inner">
                {{ r.nome }}
                <svg class="dt-sort" width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5">
                  <template v-if="sortRestId === r.id && sortOrder === 'desc'"><path d="m6 9 6 6 6-6"/></template>
                  <template v-else-if="sortRestId === r.id && sortOrder === 'asc'"><path d="m18 15-6-6-6 6"/></template>
                  <template v-else><path d="m7 15 5 5 5-5"/><path d="m7 9 5-5 5 5"/></template>
                </svg>
              </span>
            </th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="row in rows" :key="row.id"
            class="dt-row"
            :class="{ selected: isSelected(row) }"
            @click="$emit('toggle-row', row)"
          >
            <!-- Checkbox -->
            <td class="dt-td dt-td-check" @click.stop="$emit('toggle-row', row)">
              <span class="dt-cb" :class="{ on: isSelected(row) }">
                <svg v-if="isSelected(row)" width="8" height="8" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="4"><path d="M20 6 9 17l-5-5"/></svg>
              </span>
            </td>

            <!-- Cliente -->
            <td class="dt-td dt-td-name" @click.stop>
              <div class="dt-client">
                <div class="dt-avatar" :style="`background:${avatarBg(row.nome)}`">{{ initials(row.nome) }}</div>
                <div class="dt-client-info">
                  <span class="dt-client-name" @click="$emit('ver-perfil', row)">{{ row.nome }}</span>
                  <span class="dt-client-qr">{{ row.qr_code }}</span>
                </div>
              </div>
            </td>

            <!-- Descontos -->
            <td v-for="r in restaurantes" :key="r.id" class="dt-td dt-td-disc" @click.stop>
              <!-- Visualização -->
              <div
                v-if="!isEditing(row.id, r.id)"
                class="dt-disc"
                :class="discCls(row.descontos && row.descontos[r.id])"
                title="Clique para editar"
                @click="startEdit(row, r.id)"
              >
                <span class="dt-disc-val">{{ fmt(row.descontos && row.descontos[r.id]) }}</span>
                <svg class="dt-disc-edit" width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
              </div>

              <!-- Edição inline -->
              <div v-else class="dt-edit" @click.stop>
                <input
                  :ref="`inp-${row.id}-${r.id}`"
                  v-model="editBuf"
                  type="number" min="0" max="100"
                  class="dt-edit-input"
                  @keydown.enter.prevent="handleEnter(row, r.id)"
                  @keydown.esc="cancelEdit"
                  @blur="handleBlur(row, r.id)"
                />
                <span class="dt-edit-pct">%</span>
              </div>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
</template>

<script>
import { descontosApi } from "./descontos.api.js";

const COLORS = ["#ff825d","#5e72e4","#2dce89","#f59e0b","#11cdef","#f5365c","#8b5cf6","#0ea5e9"];

export default {
  name: "DescontosTabela",
  props: {
    rows:             { type: Array,   default: () => [] },
    restaurantes:     { type: Array,   default: () => [] },
    selectedIds:      { type: Object,  default: () => new Set() },
    selectedAllMode:  { type: Boolean, default: false },
    allPageSelected:  { type: Boolean, default: false },
    somePageSelected: { type: Boolean, default: false },
    totalSelected:    { type: Number,  default: 0 },
    sortRestId:       { default: null },
    sortOrder:        { default: null },
    hasFilters:       { type: Boolean, default: false },
    page:             { type: Number,  required: true },
    perPage:          { type: Number,  required: true },
    total:            { type: Number,  required: true },
  },
  data() { return { editingCell: null, editBuf: null, origVal: null, saving: false }; },
  computed: {
    from()       { return (this.page - 1) * this.perPage; },
    to()         { return Math.min(this.from + this.perPage, this.total); },
    totalPages() { return Math.ceil(this.total / this.perPage) || 1; },
    pageNums() {
      const p = this.page, t = this.totalPages, r = [];
      for (let i = Math.max(1, p-2); i <= Math.min(t, p+2); i++) r.push(i);
      return r;
    },
    perPageLocal: {
      get() { return this.perPage; },
      set(v) { this.$emit("update:perPage", v); },
    },
  },
  methods: {
    initials(n) {
      if (!n) return "?";
      return n.trim().split(/\s+/).slice(0,2).map(w=>w[0]).join("").toUpperCase();
    },
    avatarBg(n) {
      if (!n) return COLORS[0];
      let h = 0;
      for (let i = 0; i < n.length; i++) h = (h*31+n.charCodeAt(i)) % COLORS.length;
      return COLORS[Math.abs(h) % COLORS.length];
    },
    discCls(v) {
      const n = Number(v);
      if (!n) return "disc-none";
      if (n >= 20) return "disc-high";
      if (n >= 10) return "disc-mid";
      return "disc-low";
    },
    fmt(v) {
      return (!v && v !== 0) || v === 0 ? "—" : Number(v).toFixed(0) + "%";
    },
    isSelected(row) { return this.selectedAllMode || this.selectedIds.has(row.id); },
    isEditing(cId, rId) { return this.editingCell && this.editingCell.cId === cId && this.editingCell.rId === rId; },
    startEdit(row, rId) {
      this.editingCell = { cId: row.id, rId };
      this.origVal = (row.descontos && row.descontos[rId]) || 0;
      this.editBuf = this.origVal === 0 ? "" : this.origVal;
      this.$nextTick(() => {
        const ref = this.$refs["inp-" + row.id + "-" + rId];
        const el = Array.isArray(ref) ? ref[0] : ref;
        if (el) { el.focus(); el.select(); }
      });
    },
    cancelEdit() { this.editingCell = null; this.editBuf = null; this.origVal = null; },
    handleEnter(row, rId) { this._skipBlur = true; this.saveEdit(row, rId); },
    handleBlur(row, rId)  { if (this._skipBlur) { this._skipBlur = false; return; } this.saveEdit(row, rId); },
    async saveEdit(row, rId) {
      if (this.saving) return;
      const novo = this.editBuf === "" ? 0 : Number(this.editBuf);
      if (isNaN(novo) || novo < 0 || novo > 100) {
        this.$notify({ type: "danger", title: "Valor inválido", message: "O desconto deve estar entre 0% e 100%." });
        return this.cancelEdit();
      }
      if (novo === Number(this.origVal)) return this.cancelEdit();
      this.saving = true;
      try {
        const data = await descontosApi.updateDesconto(row.qr_code, rId, novo);
        if (data.success || data.partial) {
          this.$emit("row-updated", { rowId: row.id, restId: rId, value: novo });
          this.$notify({ type: "success", title: "Guardado", message: novo === 0 ? "Desconto removido." : `Desconto atualizado para ${novo}%.` });
        } else {
          this.$notify({ type: "warning", title: "Atenção", message: "Não foi possível guardar em todos os restaurantes." });
        }
      } catch { this.$notify({ type: "danger", title: "Erro", message: "Falha ao guardar o desconto. Tente novamente." }); }
      finally { this.saving = false; this.cancelEdit(); }
    },
    onSort(rId) {
      if (this.sortRestId !== rId) { this.$emit("update:sortRestId", rId); this.$emit("update:sortOrder", "desc"); }
      else if (this.sortOrder === "desc") { this.$emit("update:sortOrder", "asc"); }
      else { this.$emit("update:sortRestId", null); this.$emit("update:sortOrder", null); }
    },
  },
};
</script>

<style scoped>
.dt-wrap { font-family: 'DM Sans','Outfit',system-ui,sans-serif; background: #fff; }

/* Controls */
.dt-controls {
  display: flex; align-items: center; justify-content: space-between;
  padding: 12px 20px;
  border-bottom: 1px solid #f5f2ef;
  flex-wrap: wrap; gap: 10px;
}
.dt-left, .dt-right { display: flex; align-items: center; gap: 10px; }
.dt-perpage {
  padding: 6px 10px; border: 1.5px solid #e8e4e0;
  border-radius: 8px; font-size: 13px; background: #faf8f6;
  color: #5a534f; outline: none; cursor: pointer;
}
.dt-perpage:focus { border-color: #ff825d; }
.dt-info { font-size: 13px; color: #a09890; }
.dt-info-sel { color: #ff825d; font-weight: 600; }

/* Pagination */
.dt-pag { display: flex; gap: 3px; }
.dt-pag-btn {
  min-width: 32px; height: 32px; padding: 0 6px;
  border: 1.5px solid #e8e4e0; border-radius: 8px;
  background: #fff; font-size: 13px; color: #6b6460;
  cursor: pointer; display: flex; align-items: center; justify-content: center;
  transition: all .15s;
}
.dt-pag-btn:hover:not(:disabled) { border-color: #ff825d; color: #ff825d; }
.dt-pag-btn:disabled { opacity: .35; cursor: default; }
.dt-pag-btn.cur { background: #ff825d; border-color: #ff825d; color: #fff; font-weight: 700; }

/* Hint */
.dt-hint {
  display: flex; align-items: center; gap: 7px;
  padding: 8px 20px;
  background: #faf8f6;
  border-bottom: 1px solid #f5f2ef;
  font-size: 12.5px; color: #b0a8a0;
}

/* Empty */
.dt-empty { text-align: center; padding: 60px 20px; color: #b0a8a0; }
.dt-empty p { font-size: 15px; margin: 12px 0 16px; }
.dt-empty-btn {
  padding: 8px 18px; background: #f7f4f1; border: none;
  border-radius: 9px; font-size: 13.5px; color: #5a534f; cursor: pointer;
}
.dt-empty-btn:hover { background: #f0ece8; }

/* Table */
.dt-scroll { overflow-x: auto; -webkit-overflow-scrolling: touch; }
.dt-table { width: 100%; border-collapse: collapse; font-size: 13.5px; }

.dt-th {
  padding: 10px 14px;
  background: #faf8f6;
  border-bottom: 2px solid #f0ece8;
  font-size: 11px; font-weight: 700;
  color: #b0a8a0; text-transform: uppercase; letter-spacing: .06em;
  white-space: nowrap; text-align: left;
  user-select: none;
}
.dt-th-check { width: 48px; }
.dt-th-name  { min-width: 220px; }
.dt-th-disc  { width: 110px; text-align: center; cursor: pointer; }
.dt-th-disc:hover .dt-sort { opacity: 1; }
.dt-th-disc.sorted { color: #ff825d; }
.dt-th-disc.sorted .dt-sort { opacity: 1; color: #ff825d; }
.dt-th-inner { display: inline-flex; align-items: center; gap: 4px; }
.dt-sort { opacity: .3; transition: opacity .15s; }

/* Rows */
.dt-row { border-bottom: 1px solid #f5f2ef; cursor: pointer; transition: background .08s; }
.dt-row:hover    { background: #fdfcfb; }
.dt-row.selected { background: #fff8f5 !important; }

.dt-td { padding: 11px 14px; vertical-align: middle; }
.dt-td-check { width: 48px; cursor: pointer; }
.dt-td-disc  { text-align: center; }

/* Checkbox */
.dt-cb {
  width: 17px; height: 17px;
  border-radius: 5px;
  border: 1.5px solid #d8d0c8;
  background: #fff;
  display: inline-flex; align-items: center; justify-content: center;
  flex-shrink: 0; cursor: pointer;
  transition: all .12s;
}
.dt-cb:hover { border-color: #ff825d; }
.dt-cb.on      { background: #ff825d; border-color: #ff825d; }
.dt-cb.partial { background: #ffd8c8; border-color: #ff825d; }
.dt-cb-dash    { width: 8px; height: 2px; background: #ff825d; border-radius: 1px; }

/* Client */
.dt-client { display: flex; align-items: center; gap: 10px; }
.dt-avatar {
  width: 36px; height: 36px; border-radius: 50%;
  color: #fff; font-size: 11px; font-weight: 700;
  display: flex; align-items: center; justify-content: center; flex-shrink: 0;
}
.dt-client-info { display: flex; flex-direction: column; min-width: 0; }
.dt-client-name {
  font-size: 14px; font-weight: 600; color: #1a1210;
  cursor: pointer; transition: color .12s;
  white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
}
.dt-client-name:hover { color: #ff825d; text-decoration: underline; }
.dt-client-qr { font-size: 11px; color: #b0a8a0; font-family: monospace; margin-top: 1px; }

/* Discount cell */
.dt-disc {
  display: inline-flex; align-items: center; justify-content: center; gap: 5px;
  min-width: 52px; padding: 5px 10px;
  border-radius: 8px;
  font-size: 14px; font-weight: 600;
  cursor: pointer;
  transition: all .12s;
  position: relative;
}
.dt-disc-edit { opacity: 0; transition: opacity .15s; flex-shrink: 0; }
.dt-disc:hover .dt-disc-edit { opacity: 1; }
.dt-disc:hover { transform: scale(1.05); }

.disc-none { color: #c8c0b8; font-weight: 400; }
.disc-none:hover { background: #f7f4f1; color: #9a908a; }
.disc-low  { color: #c07800; background: #fff8e8; }
.disc-low:hover { background: #fff0c8; }
.disc-mid  { color: #c04820; background: #fff2ee; }
.disc-mid:hover { background: #ffe4d8; }
.disc-high { color: #0a7a4a; background: #e8faf2; font-weight: 700; }
.disc-high:hover { background: #d0f4e4; }

/* Inline edit */
.dt-edit { display: inline-flex; align-items: center; gap: 3px; }
.dt-edit-input {
  width: 54px; height: 34px;
  border: 2px solid #ff825d;
  border-radius: 8px;
  font-size: 15px; font-weight: 700; text-align: center;
  outline: none; background: #fff;
  box-shadow: 0 0 0 3px rgba(255,130,93,.15);
  color: #1a1210;
}
.dt-edit-pct { font-size: 12px; color: #b0a8a0; font-weight: 600; }

@media (max-width: 768px) {
  .dt-hint { display: none; }
  .dt-th-disc { min-width: 80px; }
  .dt-client-qr { display: none; }
}
</style>
